package org.ttt.autogenesis.kvisionapp

import globals.AccelByteEnv
import globals.KEnv
import globals.ServerExtendConfig
import accelbyte.user.getUserAccessToken
import org.ttt.autogenesis.network.RestRpcBridge as SharedRestRpcBridge
import org.ttt.autogenesis.network.RpcOrigin
import org.ttt.autogenesis.network.RpcRegistry

/**
 * KVision-specific RestRpcBridge that wraps the shared implementation.
 * Maintains backward compatibility while using the shared module bridge.
 */
object RestRpcBridge
{
    /**
     * Builds the connection metadata used to scope server-side AGS calls to
     * the currently authenticated player.
     *
     * @param accessToken AGS bearer token, or `null` when the client is a guest.
     * @return Authorization metadata when a non-blank token is available.
     */
    internal fun authorizationMetadata(accessToken: String?) : Map<String, String>
    {
        return accessToken?.takeIf { it.isNotBlank() }?.let { token ->
            mapOf("Authorization" to "Bearer $token")
        } ?: emptyMap()
    }

    /**
     * Returns the active invoker after a successful connection.
     */
    val rpcInvoker get() = SharedRestRpcBridge.rpcInvoker

    /**
     * Determines whether an active connection exists.
     */
    val isConnected get() = SharedRestRpcBridge.isConnected

    /**
     * Determines whether the session is ready for RPC calls.
     */
    val isSessionReady get() = SharedRestRpcBridge.isSessionReady

    /**
     * Registers client-side RPC handlers so the server can invoke them.
     *
     * @param block Configuration block executed against the shared registry.
     */
    fun registerHandlers(block : RpcRegistry.() -> Unit)
    {
        SharedRestRpcBridge.registerHandlers(block)
    }

    /**
     * Register a callback to be invoked once the SSE connection is established.
     * If already connected, the callback is invoked immediately.
     * The callback is cleared after invocation.
     */
    fun onConnected(fn: () -> Unit)
    {
        SharedRestRpcBridge.onConnected(fn)
    }

    /**
     * Connects using ServerExtendConfig for backward compatibility.
     *
     * Calling `connect` again with the same [playerId] and [accelbyteId]
     * is a no-op (the shared bridge is idempotent on the pair). Supplying
     * a different [accelbyteId] rebinds the SSE inbound to the
     * authenticated user so server-side player resolution works after
     * login. When [accelbyteId] is `null`, the current value of
     * [AccelByteEnv.userId] is used (so callers usually just call
     * `connect()` once after login and the bridge rebinds itself).
     *
     * @param playerId Optional player ID; a stable one is generated by default.
     * @param autoReconnect Whether the underlying SSE channel should
     *                      auto-reconnect on transient drops.
     * @param suppressSseLogs Quiet the verbose SSE log lines.
     * @param accelbyteId Optional AccelByte user ID; pass `null` to default
     *                    to the current [AccelByteEnv.userId].
     */
    suspend fun connect(
        playerId : String = SharedRestRpcBridge.generatePlayerId(),
        autoReconnect : Boolean = true,
        suppressSseLogs : Boolean = true,
        accelbyteId : String? = null,
        origin : RpcOrigin = RpcOrigin.GAME_CLIENT
    )
    {
        val resolvedAccelbyteId = accelbyteId ?: AccelByteEnv.userId.takeIf { it.isNotBlank() }
        SharedRestRpcBridge.connect(
            playerId = playerId,
            baseUrl = ServerExtendConfig.defaultServerUrl,
            autoReconnect = autoReconnect,
            suppressSseLogs = suppressSseLogs,
            isGuestMode = KEnv.skipLogin,
            accelbyteId = resolvedAccelbyteId,
            origin = origin,
            metadata = authorizationMetadata(getUserAccessToken()),
            metadataProvider = { authorizationMetadata(getUserAccessToken()) }
        )
    }

    /**
     * Disconnects and releases any held client resources.
     */
    suspend fun close()
    {
        SharedRestRpcBridge.close()
    }

    /**
     * Runs the given [block] with a temporary REST RPC connection that does not auto-reconnect.
     * The bridge is connected at the start (if it was not already) and closed after the block completes.
     */
    suspend fun <T> withTemporaryConnection(block : suspend () -> T) : T
    {
        val wasConnected = isConnected
        if(!wasConnected)
        {
            connect(autoReconnect = false)
        }

        try
        {
            return block()
        }
        finally
        {
            if(!wasConnected && isConnected)
            {
                SharedRestRpcBridge.awaitInFlightRequests(timeoutMillis = 2_000L)
                close()
            }
        }
    }
}
